import React, { useState, useEffect, useRef } from "react";
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import Close from '@mui/icons-material/Close';
import Add from '@mui/icons-material/Add';
import EditorCustom from "../EditorCustorm/EditorCustom";
import LoadingButtonCustom from "../LoadingButtonCustom/LoadingButtonCustom";
import Modal from "../Modal/Modal";
import { AppButton } from "../UI";

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const hasText = (html) => typeof html === 'string' && html.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, ' ').trim() !== '';

const FieldLabel = ({ children, required, hint, error }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: error ? '#dc2626' : '#334155', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {children} {required && <Box component="span" sx={{ color: '#dc2626' }}>*</Box>}
    </Typography>
    {hint && <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{hint}</Typography>}
  </Box>
);

export default function FormNoteModal({
  errors,
  setPj,
  loading,
  handleSubmit,
  modalState,
  handleOpen,
  handleClose,
  showAddButton = true,
  subtitle,
}) {
  const [comments, setComments] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    setComments("");
    setFile(null);
  }, [modalState]);

  const pickFile = (f) => {
    setFile(f || null);
    setPj(f || null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.[0]) pickFile(e.dataTransfer.files[0]);
  };

  const removeFile = () => {
    pickFile(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const editorCustomProps = {
    value: comments,
    setValue: setComments,
    handleSubmit,
    enableOnBlur: false,
  };

  const loadingButtonProps = {
    handleSubmit: () => handleSubmit(comments),
    loading,
    disabled: !hasText(comments),
  };

  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {subtitle && (
        <Typography sx={{ fontSize: '0.88rem', color: '#475569', mt: -0.5 }}>{subtitle}</Typography>
      )}

      <Box>
        <FieldLabel required error={!!errors?.commentaire}>Commentaire</FieldLabel>
        <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: `1px solid ${errors?.commentaire ? '#fca5a5' : 'transparent'}` }}>
          <EditorCustom {...editorCustomProps} />
        </Box>
        {errors?.commentaire && (
          <Typography sx={{ fontSize: '0.78rem', color: '#dc2626', mt: 0.5 }}>{errors.commentaire}</Typography>
        )}
      </Box>

      <Box>
        <FieldLabel hint="Optionnel">Pièce jointe</FieldLabel>
        <input ref={fileInput} type="file" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
        {file ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1.1, borderRadius: '10px', background: '#f0f7fd', border: '1px solid #aed6f1' }}>
            <InsertDriveFileOutlined sx={{ color: '#03407b' }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{formatSize(file.size)}</Typography>
            </Box>
            <Tooltip title="Retirer le fichier">
              <IconButton size="small" onClick={removeFile} sx={{ color: '#64748b', '&:hover': { color: '#dc2626', background: '#fef2f2' } }}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => fileInput.current?.click()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInput.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, py: 2, px: 2,
              borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s ease',
              border: `1.5px dashed ${dragging ? '#03407b' : '#cbd5e1'}`,
              background: dragging ? '#ebf4fb' : '#f8fafc',
              color: dragging ? '#03407b' : '#64748b',
              '&:hover': { borderColor: '#03407b', background: '#f0f7fd', color: '#03407b' },
              '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: 2 },
            }}
          >
            <CloudUploadOutlined />
            <Typography sx={{ fontSize: '0.88rem' }}>
              {dragging ? 'Déposez le fichier ici' : <>Glissez un fichier ici ou <b>cliquez pour choisir</b></>}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  const actions = (
    <>
      <AppButton variant="secondary" onClick={handleClose} disabled={loading}>
        Annuler
      </AppButton>
      <LoadingButtonCustom {...loadingButtonProps} />
    </>
  );

  return (
    <>
      {showAddButton && (
        <AppButton variant="primary" onClick={handleOpen} size="small" startIcon={<Add fontSize="small" />} sx={{ mb: 1.5 }}>
          Ajouter une note
        </AppButton>
      )}
      <Modal
        open={!!modalState}
        onClose={loading ? undefined : handleClose}
        title="Ajouter une note"
        maxWidth="sm"
        actions={actions}
      >
        {body}
      </Modal>
    </>
  );
}
